import React, { useEffect, useState, useMemo } from "react";

function BalanceSheetDetailed({ datagetTrialBalanceReport, parentDetails, selected_mode_table_structure }) {

    let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

    const [expandedNodes, setExpandedNodes] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        if (datagetTrialBalanceReport) {
            const allNodes = {};
            const allGroups = {};
            datagetTrialBalanceReport.forEach(row => {
                allNodes[row.NODE] = true;
                allGroups[`${row.NODE}_${row["GROUP CODE"]}`] = true;
            });
            setExpandedNodes(allNodes);
            setExpandedGroups(allGroups);
        }
    }, [datagetTrialBalanceReport]);

    // Columns excluding NODE, GROUP CODE, BALANCE
    const selectedModeColumns = useMemo(() => {
        const selectedModeName = parentDetails.mode_list_array.find(m => m.is_selected)?.mode_name;
        return selected_mode_table_structure
            .find(item => item.mode_table_name === selectedModeName)
            ?.mode_table_columns.filter(
                col => !["NODE", "GROUP CODE", "BALANCE"].includes(col.column_key_from_api)
            ) || [];
    }, [parentDetails, selected_mode_table_structure]);

    // colSpan calculation for labels (everything before DEBIT)
    const labelColSpan = useMemo(() => {
        const debitIndex = selectedModeColumns.findIndex((col) => col.column_key_from_api === "DEBIT");
        return debitIndex > 0 ? debitIndex : 1;
    }, [selectedModeColumns]);

    // Group data by NODE -> GROUP CODE
    const groupedData = useMemo(() => {
        if (!datagetTrialBalanceReport) return {};
        return datagetTrialBalanceReport.reduce((acc, row) => {
            if (!acc[row.NODE]) acc[row.NODE] = {};
            if (!acc[row.NODE][row["GROUP CODE"]]) acc[row.NODE][row["GROUP CODE"]] = [];
            acc[row.NODE][row["GROUP CODE"]].push(row);
            return acc;
        }, {});
    }, [datagetTrialBalanceReport]);

    // Compute DEBIT or CREDIT based on BALANCE
    const formatRow = row => {
        const balance = row.BALANCE || 0;
        return {
            ...row,
            DEBIT: balance > 0 ? balance : null,
            CREDIT: balance < 0 ? Math.abs(balance) : null,
        };
    };

    const computeSubtotal = rows => {
        return rows.reduce(
            (acc, r) => {
                const formatted = formatRow(r);
                return {
                    DEBIT: acc.DEBIT + (formatted.DEBIT || 0),
                    CREDIT: acc.CREDIT + (formatted.CREDIT || 0),
                };
            },
            { DEBIT: 0, CREDIT: 0 }
        );
    };

    const grandTotal = useMemo(() => {
        if (!datagetTrialBalanceReport) return { DEBIT: 0, CREDIT: 0 };
        return datagetTrialBalanceReport.reduce(
            (acc, r) => {
                const formatted = formatRow(r);
                return {
                    DEBIT: acc.DEBIT + (formatted.DEBIT || 0),
                    CREDIT: acc.CREDIT + (formatted.CREDIT || 0),
                };
            },
            { DEBIT: 0, CREDIT: 0 }
        );
    }, [datagetTrialBalanceReport]);

    return (
        <div className="min-h-screen bg-gray-200 py-10 px-4">
            <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-none border border-gray-300 px-4 pt-4 pb-6 overflow-x-auto">

                {/* Title Banner Header */}
                <div>

                    <div>


                        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
                            <img
                                src={apiPaths_from_local_storage[0].REP_HEADER}
                            />
                        )}

                    </div>

                    <div style={{
                        fontSize: '18px',
                        fontWeight: '500',
                        color: '#0f172a',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        margin: '20px 0',
                        padding: '10px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px'
                    }}>
                        {
                            selected_mode_table_structure.find(
                                (item) => item.mode_table_code === parentDetails.mode_list_array.find((m) => m.is_selected)?.mode_code
                            )?.mode_table_name
                        }
                    </div>

                </div>

                <table className="min-w-full divide-y divide-gray-200 text-sm mt-3">
                    <thead className="border-b-2 border-gray-900">
                        <tr>
                            {selectedModeColumns.map((col, i) => (
                                <th
                                    key={i}
                                    className={`px-4 py-2 text-xs font-bold text-gray-900 uppercase tracking-wider ${
                                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api) ? "text-right" : "text-left"
                                    }`}
                                >
                                    {col.column_name}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">
                        {Object.entries(groupedData).map(([node, groups]) => {
                            const nodeSubtotal = computeSubtotal(Object.values(groups).flat());

                            return (
                                <React.Fragment key={node}>
                                    {/* NODE HEADER */}
                                    <tr
                                        className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                                        onClick={() => setExpandedNodes(prev => ({ ...prev, [node]: !prev[node] }))}
                                    >
                                        <td colSpan={selectedModeColumns.length} className="px-4 py-2 font-bold text-gray-900 text-sm flex items-center gap-2 uppercase tracking-wide">
                                            <span
                                                className={`transition-transform duration-200 ${expandedNodes[node] ? "rotate-90" : "rotate-0"}`}
                                            >
                                                ▶
                                            </span>
                                            {node}
                                        </td>
                                    </tr>

                                    {/* GROUPS */}
                                    {expandedNodes[node] &&
                                        Object.entries(groups).map(([groupCode, rows]) => {
                                            const groupSubtotal = computeSubtotal(rows);
                                            const groupKey = `${node}_${groupCode}`;

                                            return (
                                                <React.Fragment key={groupKey}>
                                                    {/* GROUP HEADER */}
                                                    <tr
                                                        className="border-t border-gray-100 cursor-pointer hover:bg-gray-50 text-gray-800"
                                                        onClick={() => setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                                    >
                                                        <td colSpan={selectedModeColumns.length} className="pl-8 pr-4 py-1.5 font-semibold text-xs flex items-center gap-2 uppercase tracking-wider text-gray-700">
                                                            <span
                                                                className={`transition-transform duration-200 ${expandedGroups[groupKey] ? "rotate-90" : "rotate-0"}`}
                                                            >
                                                                ▶
                                                            </span>
                                                            Group Code: {groupCode}
                                                        </td>
                                                    </tr>

                                                    {/* ACCOUNTS UNDER GROUP */}
                                                    {expandedGroups[groupKey] &&
                                                        rows.map((row, i) => {
                                                            const formatted = formatRow(row);
                                                            return (
                                                                <tr key={i} className="hover:bg-gray-50">
                                                                    {selectedModeColumns.map((col, ci) => {
                                                                        const key = col.column_key_from_api;
                                                                        return (
                                                                            <td
                                                                                key={ci}
                                                                                className={`px-4 py-1.5 text-sm ${
                                                                                    ["DEBIT", "CREDIT"].includes(key)
                                                                                        ? "text-right tabular-nums font-mono"
                                                                                        : "text-left text-gray-700"
                                                                                } ${
                                                                                    key === "DEBIT"
                                                                                        ? "text-blue-700"
                                                                                        : key === "CREDIT"
                                                                                        ? "text-emerald-700"
                                                                                        : ""
                                                                                }`}
                                                                            >
                                                                                {key === "DEBIT"
                                                                                    ? (formatted.DEBIT !== null ? formatted.DEBIT.toFixed(2) : "")
                                                                                    : key === "CREDIT"
                                                                                    ? (formatted.CREDIT !== null ? formatted.CREDIT.toFixed(2) : "")
                                                                                    : row[key]}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}

                                                    {/* GROUP SUBTOTAL */}
                                                    {expandedGroups[groupKey] && (
                                                        <tr className="border-t border-dashed border-gray-200 font-medium text-xs">
                                                            <td colSpan={labelColSpan} className="pl-12 pr-4 py-1.5 italic text-gray-500">
                                                                Subtotal — {groupCode}
                                                            </td>
                                                            <td className="px-4 py-1.5 text-right tabular-nums font-mono text-blue-700">
                                                                {groupSubtotal.DEBIT > 0 ? groupSubtotal.DEBIT.toFixed(2) : ""}
                                                            </td>
                                                            <td className="px-4 py-1.5 text-right tabular-nums font-mono text-emerald-700">
                                                                {groupSubtotal.CREDIT > 0 ? groupSubtotal.CREDIT.toFixed(2) : ""}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}

                                    {/* NODE SUBTOTAL */}
                                    {expandedNodes[node] && (
                                        <tr className="border-t border-dashed border-gray-300 font-medium text-xs">
                                            <td colSpan={labelColSpan} className="px-4 py-2 italic text-gray-600 font-semibold">
                                                Node Total — {node}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-blue-700 font-semibold">
                                                {nodeSubtotal.DEBIT > 0 ? nodeSubtotal.DEBIT.toFixed(2) : ""}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-emerald-700 font-semibold">
                                                {nodeSubtotal.CREDIT > 0 ? nodeSubtotal.CREDIT.toFixed(2) : ""}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {/* GRAND TOTAL */}
                        <tr className="font-bold text-sm" style={{ borderTop: "3px double #111827" }}>
                            <td colSpan={labelColSpan} className="px-4 py-3 text-gray-900 uppercase tracking-wide text-xs">
                                Grand Total
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-mono text-blue-800">
                                {grandTotal.DEBIT > 0 ? grandTotal.DEBIT.toFixed(2) : ""}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-mono text-emerald-800">
                                {grandTotal.CREDIT > 0 ? grandTotal.CREDIT.toFixed(2) : ""}
                            </td>
                        </tr>

                        {/* DIFFERENCE ROW (IF UNBALANCED) */}
                        {grandTotal.DEBIT !== grandTotal.CREDIT && (
                            <tr className="bg-red-100 font-semibold border-t border-red-300">
                                <td colSpan={labelColSpan} className="px-4 py-2 text-red-800 uppercase tracking-wide text-xs">
                                    Difference
                                </td>
                                <td colSpan="2" className="px-4 py-2 text-right tabular-nums font-mono text-red-700">
                                    {Math.abs(grandTotal.DEBIT - grandTotal.CREDIT).toFixed(2)}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Report Footer */}
                <div className="mt-8">
                    <div className="border-t-2 border-gray-900" />
                    <div className="mt-0.5 border-t border-gray-300" />
                    <div className="mt-4 flex justify-between items-center">
                        <p className="text-xs text-gray-400">
                            Generated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default BalanceSheetDetailed;